<template>
    <div>
        <div class="action-plan-panel">
            <!-- Sin item seleccionado -->
            <div v-if="!item" class="empty-panel">
                <div class="empty-content">
                    <i class="fa fa-hand-pointer-o fa-3x"></i>
                    <h5>Selecciona un plan de acción</h5>
                    <p>Elige un item del panel izquierdo para ver y editar su plan de acción</p>
                </div>
            </div>

            <!-- Con item seleccionado -->
            <template v-else>
                <!-- Header con info del item -->
                <div class="panel-header">
                    <div class="header-content">
                        <h5 class="item-title">{{ item.name }}</h5>
                        <p class="item-breadcrumb">
                            Pilar: {{ pillarName }} / Elemento: {{ elementName }}
                        </p>
                        <!-- Estado Badge debajo del breadcrumb -->
                        <div class="status-badge-header"
                            :style="{ backgroundColor: currentStatusDef.bg_color, color: currentStatusDef.text_color }">
                            <i :class="'fa ' + currentStatusDef.icon"></i>
                            <span>{{ currentStatusDef.label }}</span>
                        </div>
                    </div>
                    <!-- Botones de acciones -->
                    <div class="header-buttons">
                        <!-- Botón de notificaciones -->
                        <button class="notification-button" @click="showNotificationConfig = !showNotificationConfig"
                            :class="{ 'active': showNotificationConfig, 'notifications-enabled': item.plan_action && item.plan_action.apply_notification }"
                            title="Configurar notificaciones">
                            <i class="fa fa-bell"></i>
                        </button>

                        <!-- Botón info para ver más detalles -->
                        <button class="info-button" @click="showItemDetails = !showItemDetails"
                            :class="{ 'active': showItemDetails }" title="Ver más información del item">
                            <i class="fa fa-info-circle"></i>
                        </button>
                    </div>
                </div>

                <!-- Panel de configuración de notificaciones -->
                <notification-config-panel
                    v-if="showNotificationConfig"
                    :apply-notification="item.plan_action && item.plan_action.apply_notification"
                    :selected-frequencies="selectedFrequencies"
                    :frequency-definitions="notificationFrequencyDefinitions"
                    @toggle-notifications="toggleNotifications"
                    @update-frequencies="updateFrequencies"
                />

                <!-- Panel de detalles adicionales del item -->
                <div v-if="showItemDetails" class="item-details-panel">
                    <table class="details-table">
                        <tbody>
                            <tr v-if="item.question">
                                <td class="detail-label">Pregunta:</td>
                                <td class="detail-value">{{ item.question }}</td>
                            </tr>
                            <tr v-if="item.cross_cutting_criteria && item.cross_cutting_criteria.length">
                                <td class="detail-label">Criterio transversal:</td>
                                <td class="detail-value">
                                    {{item.cross_cutting_criteria.map(c => c.name).join(', ')}}
                                </td>
                            </tr>
                            <tr v-if="item.tour_routes && item.tour_routes.length">
                                <td class="detail-label">Ruta de visita:</td>
                                <td class="detail-value">
                                    {{item.tour_routes.map(r => r.name).join(', ')}}
                                </td>
                            </tr>
                            <tr v-if="item.default_legislation">
                                <td class="detail-label">Legislación que aplica:</td>
                                <td class="detail-value">{{ item.default_legislation }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Formulario de plan de acción -->
                <div class="panel-body">
                    <!-- Prioridad y Fecha en 2 columnas -->
                    <div class="form-row-grid-2">
                        <!-- Prioridad con AI Suggestion debajo -->
                        <div class="form-group mb-3">
                            <div class="priority-label-row">
                                <label class="field-label mb-0">Prioridad</label>
                                <div v-if="wasAiSuggestionAccepted" class="ai-reset-link" @click="resetAiSuggestion">
                                    <i class="fa fa-magic"></i>
                                    <span>Activar sugerencias de IA</span>
                                </div>
                            </div>
                            <b-form-select v-model="localPlan.priority" @change="handleImmediateInput" class="custom-select">
                                <b-form-select-option v-for="priority in priorityDefinitions" :key="priority.value"
                                    :value="priority.value">
                                    {{ priority.label }}
                                </b-form-select-option>
                            </b-form-select>

                            <!-- AI Suggestion Card solo debajo de prioridad -->
                            <div v-if="showAiSuggestion" class="ai-suggestion-card">
                                <div class="ai-suggestion-header">
                                    <i class="fa fa-magic ai-suggestion-icon"></i>
                                    <span class="ai-suggestion-title">Sugerencia de IA</span>
                                </div>
                                <p class="ai-suggestion-text">
                                    Se recomienda prioridad:
                                    <span class="ai-priority-value" :style="getAiPriorityStyle()">
                                        <i :class="'fa ' + getAiPriorityIcon()"></i>
                                        {{ item.plan_action.priority_suggested_by_ai }}
                                    </span>
                                </p>
                                <div class="ai-suggestion-actions">
                                    <button class="ai-btn ai-btn-accept" @click="acceptAiSuggestion">
                                        <i class="fa fa-check"></i> Aceptar
                                    </button>
                                    <button class="ai-btn ai-btn-reject" @click="rejectAiSuggestion">
                                        <i class="fa fa-times"></i> Rechazar
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Fecha de vencimiento -->
                        <div class="form-group mb-3">
                            <label class="field-label">Fecha de vencimiento</label>
                            <b-form-input v-model="localPlan.end_date" type="date" @input="handleImmediateInput" />
                        </div>
                    </div>

                    <!-- Responsable solo -->
                    <div class="form-group">
                        <label class="field-label">Responsable</label>
                        <div class="select-with-action">
                            <b-form-select v-model="localPlan.responsible_id" @change="handleImmediateInput">
                                <b-form-select-option :value="null">
                                    Seleccione un responsable
                                </b-form-select-option>
                                <b-form-select-option
                                    v-if="currentResponsible && !workers.find(w => w.id === currentResponsible.id)"
                                    :value="currentResponsible.id">
                                    {{ currentResponsible.names }} {{ currentResponsible.surnames }}
                                </b-form-select-option>
                                <b-form-select-option v-for="worker in workers" :key="worker.id" :value="worker.id">
                                    {{ worker.names }} {{ worker.surnames }}
                                </b-form-select-option>
                            </b-form-select>
                            <b-button variant="primary" size="sm" :disabled="!companyId" @click="openWorkerModal"
                                title="Crear nuevo trabajador">
                                <i class="fa fa-plus"></i>
                            </b-button>
                        </div>
                    </div>

                    <!-- Información del Diagnóstico (solo lectura) - ANTES de Acción Correctiva -->
                    <div class="readonly-section">
                        <div class="section-header">
                            <h6 class="section-title">Información del Diagnóstico</h6>
                            <!-- Rating badge -->
                            <span v-if="item.response && item.response.rating" class="rating-badge"
                                :style="{ backgroundColor: item.response.rating.color || '#6B7280' }">
                                {{ item.response.rating.name }} ({{ item.response.rating.rating }})
                            </span>
                        </div>

                        <!-- Se evidencia con -->
                        <div v-if="item.response && item.response.observations" class="info-field">
                            <label>Se evidencia con</label>
                            <div class="info-content">{{ item.response.observations }}</div>
                        </div>
                    </div>

                    <!-- Acción Correctiva -->
                    <div class="form-group">
                        <label class="field-label">
                            Acción Correctiva
                            <span class="optional-badge">Editable</span>
                        </label>
                        <b-form-textarea v-model="localPlan.corrective_action"
                            placeholder="Describa la acción correctiva..." rows="4" @input="handleInput" />
                    </div>

                    <!-- Archivos de Evidencia -->
                    <div v-if="hasDocuments" class="documents-section">
                        <h6 class="section-title">
                            <i class="fa fa-folder-open-o mr-2"></i>
                            Archivos de Evidencia
                        </h6>
                        <div class="documents-list">
                            <div v-for="doc in itemDocuments" :key="doc.id" class="document-item">
                                <div class="document-icon">
                                    <i :class="getFileIcon(doc.original_name || doc.custom_name)"></i>
                                </div>
                                <div class="document-info">
                                    <span class="document-name">{{ doc.custom_name || doc.original_name }}</span>
                                    <span v-if="doc.original_name && doc.custom_name" class="document-original">
                                        {{ doc.original_name }}
                                    </span>
                                </div>
                                <button class="document-download" @click="downloadDocument(doc)" title="Descargar">
                                    <i class="fa fa-download"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Indicador de guardado -->
                    <div v-if="saving" class="saving-indicator">
                        <i class="fa fa-spinner fa-spin mr-2"></i>
                        Guardando cambios...
                    </div>
                </div>
            </template>
        </div>

        <!-- Modal para crear trabajador -->
        <b-modal id="modal-action-plan-worker" title="Crear trabajador" hide-footer size="lg"
            @hidden="handleWorkerModalHidden">
            <FormWorker :company_id="companyId" :showAddPosition="true" :dni_id_required="false" module="DIAGNOSTICOS"
                @saveWorker="handleWorkerSave" />
        </b-modal>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import FormWorker from '../../../../workers/workers/components/FormWorker.vue';
import NotificationConfigPanel from '../../../components/NotificationConfigPanel.vue';

export default {
    name: 'ActionPlanDetailPanel',
    components: { 
        FormWorker,
        NotificationConfigPanel
    },
    props: {
        item: {
            type: Object,
            default: null
        },
        pillarName: {
            type: String,
            default: ''
        },
        elementName: {
            type: String,
            default: ''
        },
        companyId: {
            type: [Number, String],
            default: null
        },
        workers: {
            type: Array,
            default: () => []
        },
        statusDefinitions: {
            type: Array,
            default: () => []
        },
        priorityDefinitions: {
            type: Array,
            default: () => [
                { value: 'Alto', label: 'Alto', color: '#DC2626', bg_color: '#FEE2E2', text_color: '#991B1B', icon: 'fa-arrow-up' },
                { value: 'Medio', label: 'Medio', color: '#F59E0B', bg_color: '#FEF3C7', text_color: '#92400E', icon: 'fa-minus' },
                { value: 'Bajo', label: 'Bajo', color: '#22C55E', bg_color: '#D1FAE5', text_color: '#065F46', icon: 'fa-arrow-down' }
            ]
        },
        notificationFrequencyDefinitions: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            localPlan: {
                corrective_action: '',
                end_date: null,
                responsible_id: null,
                status: 'Abierto',
                priority: 'Medio'
            },
            originalPlan: {},
            saving: false,
            saveTimeout: null,
            showItemDetails: false,
            showNotificationConfig: false,
            selectedFrequencies: []
        };
    },
    computed: {
        selectedWorker() {
            return this.workers.find(w => w.id === this.localPlan.responsible_id);
        },
        currentResponsible() {
            if (this.item && this.item.plan_action && this.item.plan_action.responsible) {
                return this.item.plan_action.responsible;
            }
            return null;
        },
        hasChanges() {
            return this.localPlan.corrective_action !== this.originalPlan.corrective_action ||
                this.localPlan.end_date !== this.originalPlan.end_date ||
                this.localPlan.responsible_id !== this.originalPlan.responsible_id ||
                this.localPlan.status !== this.originalPlan.status ||
                this.localPlan.priority !== this.originalPlan.priority;
        },
        showAiSuggestion() {
            return this.item &&
                this.item.plan_action &&
                this.item.plan_action.is_suggested_by_ai &&
                this.item.plan_action.priority_suggested_by_ai &&
                !this.item.plan_action.is_accepted_suggested_by_ai &&
                this.item.plan_action.priority !== this.item.plan_action.priority_suggested_by_ai;
        },
        wasAiSuggestionAccepted() {
            return this.item &&
                this.item.plan_action &&
                this.item.plan_action.is_accepted_suggested_by_ai;
        },
        canAcceptAiSuggestion() {
            return this.showAiSuggestion &&
                this.localPlan.priority !== this.item.plan_action.priority_suggested_by_ai;
        },
        currentStatusDef() {
            const status = (this.item && this.item.plan_action && this.item.plan_action.status) ? this.item.plan_action.status : 'Abierto';
            return this.statusDefinitions.find(s => s.value === status) || { label: status, color: '#6B7280', bg_color: '#F3F4F6', text_color: '#374151', icon: 'fa-folder-open' };
        },
        itemDocuments() {
            if (this.item && this.item.response && this.item.response.documents) {
                return this.item.response.documents;
            }
            return [];
        },
        hasDocuments() {
            return this.itemDocuments.length > 0;
        },
        availableStatuses() {
            if (this.statusDefinitions && this.statusDefinitions.length > 0) {
                return this.statusDefinitions;
            }
            // Fallback si statusDefinitions no está disponible
            return [
                { value: 'Abierto', label: 'Abierto' },
                { value: 'Proceso', label: 'En Proceso' },
                { value: 'Cerrado', label: 'Cerrado' },
                { value: 'Cerrado por verificar', label: 'Por verificar' }
            ];
        }
    },
    watch: {
        item: {
            immediate: true,
            handler(newItem) {
                if (newItem && newItem.plan_action) {
                    this.loadPlanData();
                }
            }
        }
    },
    methods: {
        ...mapActions('skudo/diagnostics', ['updateActionPlan', 'downloadItemResponseAttachment']),
        ...mapActions('workers', ['saveWorker']),
        ...mapMutations('workers', ['resetWorker']),

        loadPlanData() {
            if (!this.item || !this.item.plan_action) return;

            const plan = this.item.plan_action;
            this.localPlan = {
                corrective_action: plan.corrective_action || '',
                end_date: plan.end_date || null,
                responsible_id: plan.responsible_id || null,
                status: plan.status || 'Abierto',
                priority: plan.priority || 'Medio'
            };

            // Frecuencias de notificación
            if (plan.config_notifications && Array.isArray(plan.config_notifications)) {
                this.selectedFrequencies = plan.config_notifications.map(n => n.notification_frequency_id);
            } else {
                this.selectedFrequencies = [];
            }

            this.originalPlan = { ...this.localPlan };
        },

        async toggleNotifications(newValue) {
            if (!this.item || !this.item.plan_action) return;

            // Optimistic update
            const previousValue = this.item.plan_action.apply_notification;
            this.$set(this.item.plan_action, 'apply_notification', newValue);

            try {
                await this.updateActionPlan({
                    id: this.item.plan_action.id,
                    data: {
                        company_id: this.companyId,
                        apply_notification: newValue,
                        // Enviar siempre las frecuencias seleccionadas para no perderlas si se desactiva
                        notification_frequencies: this.selectedFrequencies
                    }
                });

                this.$emit('updated');
            } catch (error) {
                console.error(error);
                // Revertir cambio en caso de error
                this.$set(this.item.plan_action, 'apply_notification', previousValue);
                this.$alertError('Error al actualizar la configuración de notificaciones');
            }
        },

        async updateFrequencies(val) {
            if (!this.item || !this.item.plan_action) return;

            // Actualizar el valor local inmediatamente para que el componente responda
            this.selectedFrequencies = val;

            try {
                await this.updateActionPlan({
                    id: this.item.plan_action.id,
                    data: {
                        company_id: this.companyId,
                        // El backend requiere apply_notification = true para actualizar frecuencias
                        apply_notification: true,
                        notification_frequencies: val
                    }
                });

                const newConfigs = val.map(id => ({
                    id: Math.random(),
                    notification_frequency_id: id,
                    notification_frequency: this.notificationFrequencyDefinitions.find(f => f.id === id)
                }));

                this.$set(this.item.plan_action, 'config_notifications', newConfigs);
                this.$emit('updated');
            } catch (error) {
                console.error(error);
                this.$alertError('Error al actualizar las frecuencias');
            }
        },

        handleInput() {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }

            this.saveTimeout = setTimeout(() => {
                if (this.hasChanges) {
                    this.saveChanges();
                }
            }, 1000);
        },

        handleImmediateInput() {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }
            // Pequeño delay de seguridad (50ms) para asegurar que el v-model actualizó
            this.saveTimeout = setTimeout(() => {
                if (this.hasChanges) {
                    this.saveChanges();
                }
            }, 50);
        },

        async saveChanges() {
            if (!this.companyId || !this.item || !this.item.plan_action || !this.item.plan_action.id) return;

            this.saving = true;

            // Ocultar indicador rápidamente sin esperar respuesta completa
            setTimeout(() => {
                this.saving = false;
            }, 300);

            try {
                await this.updateActionPlan({
                    id: this.item.plan_action.id,
                    data: {
                        company_id: this.companyId,
                        corrective_action: this.localPlan.corrective_action,
                        end_date: this.localPlan.end_date,
                        responsible_id: this.localPlan.responsible_id,
                        status: this.localPlan.status,
                        priority: this.localPlan.priority
                    }
                });

                // Detectar cambio de responsable
                if (this.localPlan.responsible_id !== this.originalPlan.responsible_id) {
                    this.$emit('responsible-changed', {
                        planActionId: this.item.plan_action.id,
                        planActionName: this.item.name,
                        correctiveAction: this.localPlan.corrective_action,
                        responsibleId: this.localPlan.responsible_id
                    });
                }

                this.originalPlan = { ...this.localPlan };
                this.$emit('updated');
            } catch (error) {
                this.$alertError('Error al actualizar el plan de acción');
            }
        },

        async acceptAiSuggestion() {
            if (this.item && this.item.plan_action && this.item.plan_action.priority_suggested_by_ai) {
                this.localPlan.priority = this.item.plan_action.priority_suggested_by_ai;

                // Guardar con is_accepted_suggested_by_ai = true
                try {
                    await this.updateActionPlan({
                        id: this.item.plan_action.id,
                        data: {
                            company_id: this.companyId,
                            priority: this.localPlan.priority,
                            is_accepted_suggested_by_ai: true
                        }
                    });
                    this.$set(this.item.plan_action, 'is_accepted_suggested_by_ai', true);
                    this.originalPlan.priority = this.localPlan.priority;
                    this.$emit('updated');
                } catch (error) {
                    this.$alertError('Error al aceptar sugerencia');
                }
            }
        },
        async rejectAiSuggestion() {
            // Rechazar sugerencia - guardar en backend para no volver a sugerir
            if (this.item && this.item.plan_action) {
                try {
                    await this.updateActionPlan({
                        id: this.item.plan_action.id,
                        data: {
                            company_id: this.companyId,
                            is_accepted_suggested_by_ai: true
                        }
                    });
                    this.$set(this.item.plan_action, 'is_accepted_suggested_by_ai', true);
                    this.$emit('updated');
                } catch (error) {
                    this.$alertError('Error al rechazar sugerencia');
                }
            }
        },
        async resetAiSuggestion() {
            // Permitir recibir nueva sugerencia de IA
            if (this.item && this.item.plan_action) {
                try {
                    await this.updateActionPlan({
                        id: this.item.plan_action.id,
                        data: {
                            company_id: this.companyId,
                            is_accepted_suggested_by_ai: false
                        }
                    });
                    this.$set(this.item.plan_action, 'is_accepted_suggested_by_ai', false);
                    this.$emit('updated');
                } catch (error) {
                    this.$alertError('Error al resetear sugerencia');
                }
            }
        },
        getAiPriorityStyle() {
            const priority = (this.item && this.item.plan_action) ? this.item.plan_action.priority_suggested_by_ai : null;
            const def = this.priorityDefinitions.find(p => p.value === priority);
            if (!def) return {};
            return {
                backgroundColor: def.bg_color,
                color: def.text_color,
                borderColor: def.color
            };
        },
        getAiPriorityIcon() {
            const priority = (this.item && this.item.plan_action) ? this.item.plan_action.priority_suggested_by_ai : null;
            const def = this.priorityDefinitions.find(p => p.value === priority);
            return def ? def.icon : 'fa-minus';
        },
        getFileIcon(fileName) {
            if (!fileName) return 'fa fa-file-o text-secondary';
            const name = fileName.toLowerCase();

            if (name.endsWith('.pdf')) return 'fa fa-file-pdf-o text-danger';
            if (name.endsWith('.doc') || name.endsWith('.docx')) return 'fa fa-file-word-o text-primary';
            if (name.endsWith('.xls') || name.endsWith('.xlsx')) return 'fa fa-file-excel-o text-success';
            if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.gif')) return 'fa fa-file-image-o text-info';
            return 'fa fa-file-o text-secondary';
        },

        async downloadDocument(doc) {
            if (!doc || !doc.id) {
                this.$alertError('No se pudo obtener la información del archivo');
                return;
            }

            try {
                const response = await this.downloadItemResponseAttachment({
                    attachmentId: doc.id,
                    companyId: this.companyId
                });

                if (response && response.data) {
                    const { base64, mime_type, original_name } = response.data;

                    const byteCharacters = atob(base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: mime_type });

                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = original_name || doc.original_name;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }
            } catch (error) {
                this.$alertError('Error al descargar el archivo');
            }
        },

        openWorkerModal() {
            if (!this.companyId) {
                this.$alertWarning('No se ha seleccionado una empresa');
                return;
            }
            this.resetWorker();
            this.$bvModal.show('modal-action-plan-worker');
        },

        async handleWorkerSave() {
            if (!this.companyId) {
                this.$alertWarning('No se ha seleccionado una empresa');
                return;
            }

            try {
                await this.saveWorker(this.companyId);
                this.$bvModal.hide('modal-action-plan-worker');
                this.$emit('worker-created');
            } catch (error) {
                this.$alertError('Error al crear el trabajador');
            }
        },

        handleWorkerModalHidden() {
            // Limpieza después de cerrar el modal
        }
    },
    beforeDestroy() {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
    }
};
</script>

<style scoped>
.action-plan-panel {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #E5E7EB;
    min-height: 500px;
}

.empty-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 500px;
    padding: 40px;
}

.empty-content {
    text-align: center;
    color: #6B7280;
}

.empty-content i {
    color: #D1D5DB;
    margin-bottom: 16px;
}

.empty-content h5 {
    color: #374151;
    margin-bottom: 8px;
}

.empty-content p {
    font-size: 14px;
    margin: 0;
}

.panel-header {
    padding: 20px 24px;
    border-bottom: 1px solid #E5E7EB;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.header-content {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.info-button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid #E5E7EB;
    background: #ffffff;
    color: #6B7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.info-button:hover {
    background: #F3F4F6;
    color: #374151;
}

.info-button.active {
    background: #3B82F6;
    color: white;
    border-color: #3B82F6;
}

.item-details-panel {
    background: #F9FAFB;
    padding: 16px 24px;
    border-bottom: 1px solid #E5E7EB;
}

.details-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.detail-item label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #6B7280;
    margin-bottom: 4px;
}

.detail-item p {
    margin: 0;
    font-size: 14px;
    color: #374151;
    line-height: 1.5;
}

.form-row-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.item-title {
    font-size: 16px;
    font-weight: 500;
    color: #1F2937;
    margin: 0 0 8px 0;
    line-height: 1.4;
}

.item-breadcrumb {
    font-size: 12px;
    color: #495263;
    margin: 0;
}

.panel-body {
    padding: 24px;
}

.form-row {
    display: flex;
    gap: 16px;
    margin-bottom: 0;
}

.form-group {
    margin-bottom: 20px;
}

.field-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.optional-badge {
    font-size: 11px;
    font-weight: 500;
    color: #6B7280;
    background: #F3F4F6;
    padding: 2px 8px;
    border-radius: 10px;
}

.readonly-section {
    margin-bottom: 20px;
    padding: 16px;
    background: #F9FAFB;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
}

.section-title {
    font-size: 14px;
    font-weight: 600;
    color: #6B7280;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.info-field {
    margin-bottom: 16px;
}

.info-field label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #6B7280;
    margin-bottom: 6px;
}

.info-content {
    background: #F9FAFB;
    padding: 12px;
    border-radius: 6px;
    font-size: 14px;
    color: #374151;
    line-height: 1.5;
}

.rating-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 13px;
    font-weight: 600;
    color: white;
}

.saving-indicator {
    text-align: center;
    padding: 12px;
    background: #F3F4F6;
    border-radius: 6px;
    font-size: 13px;
    color: #6B7280;
    margin-top: 16px;
}

/* Custom form styles */
.custom-select,
.form-control {
    border-radius: 6px;
    border-color: #D1D5DB;
}

.custom-select:focus,
.form-control:focus {
    border-color: #10B981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

/* Section header with rating badge */
.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.section-header .section-title {
    margin-bottom: 0;
}

/* Details table */
.details-table {
    width: 100%;
    border-collapse: collapse;
}

.details-table tr {
    border-bottom: 1px solid #E5E7EB;
}

.details-table tr:last-child {
    border-bottom: none;
}

.detail-label {
    font-size: 12px;
    font-weight: 600;
    color: #6B7280;
    padding: 8px 12px 8px 0;
    vertical-align: top;
    width: 140px;
    white-space: nowrap;
}

.detail-value {
    font-size: 14px;
    color: #374151;
    padding: 8px 0;
    line-height: 1.5;
}

/* Documents section */
.documents-section {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #E5E7EB;
}

.documents-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.document-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: #F9FAFB;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
}

.document-icon {
    font-size: 24px;
    width: 32px;
    text-align: center;
}

.document-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.document-name {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.document-original {
    font-size: 12px;
    color: #9CA3AF;
}

.document-download {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid #D1D5DB;
    background: #ffffff;
    color: #10B981;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.document-download:hover {
    background: #10B981;
    color: white;
    border-color: #10B981;
}

/* Select with action button */
.select-with-action {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    align-items: center;
}

.select-with-action .custom-select,
.select-with-action select {
    flex: 1;
}

.select-with-action .btn {
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
}

/* Grid 2 columns */
.form-row-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

/* Status badge in header */
.status-badge-header {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    margin-top: 8px;
    width: fit-content;
}

/* AI Suggestion Card */
.ai-suggestion-card {
    background: #E8F4FD;
    border: 1px solid #B8DFFB;
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 10px;
}

.ai-suggestion-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.ai-suggestion-icon {
    color: #3B82F6;
    font-size: 14px;
}

.ai-suggestion-title {
    font-size: 13px;
    font-weight: 600;
    color: #1E40AF;
}

.ai-suggestion-text {
    font-size: 13px;
    color: #374151;
    margin: 0 0 10px 0;
}

.ai-priority-value {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 16px;
    font-weight: 600;
    font-size: 12px;
    border: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.ai-priority-value i {
    font-size: 11px;
}

.ai-suggestion-actions {
    display: flex;
    gap: 8px;
}

.ai-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
}

.ai-btn-accept {
    background: #3B82F6;
    color: white;
}

.ai-btn-accept:hover {
    background: #2563EB;
}

.ai-btn-reject {
    background: white;
    color: #6B7280;
    border: 1px solid #D1D5DB;
}

.ai-btn-reject:hover {
    background: #F3F4F6;
}

.ai-accepted-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    background: #D1FAE5;
    color: #065F46;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 500;
}

/* Priority label row */
.priority-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}

/* AI Reset Link */
.ai-reset-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    background: #E8F4FD;
    color: #3B82F6;
    border: 1px solid #B8DFFB;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.ai-reset-link:hover {
    background: #3B82F6;
    color: white;
    border-color: #3B82F6;
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.25);
}

.ai-reset-link i {
    font-size: 11px;
}

/* Header buttons container */
.header-buttons {
    display: flex;
    gap: 8px;
}

/* Notification Button */
.notification-button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid #E5E7EB;
    background: #ffffff;
    color: #6B7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.notification-button:hover {
    background: #F3F4F6;
    color: #374151;
}

.notification-button.active {
    background: #3B82F6;
    color: white;
    border-color: #3B82F6;
}

.notification-button.notifications-enabled {
    color: #10B981;
    /* Green */
    border-color: #34D399;
    background: #ECFDF5;
}

.notification-button.notifications-enabled.active {
    background: #10B981;
    color: white;
    border-color: #10B981;
}

/* Notification Config Panel */
.notification-config-panel {
    background: #F8FAFC;
    padding: 16px 24px;
    border-bottom: 1px solid #E2E8F0;
    animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.config-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.config-header h6 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
}

/* Toggle Switch */
.toggle-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin: 0;
}

.toggle-label input {
    display: none;
}

.toggle-slider {
    position: relative;
    width: 36px;
    height: 20px;
    background-color: #CBD5E1;
    border-radius: 20px;
    transition: .3s;
    margin-right: 8px;
}

.toggle-slider:before {
    content: "";
    position: absolute;
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    border-radius: 50%;
    transition: .3s;
}

.toggle-label input:checked+.toggle-slider {
    background-color: #10B981;
}

.toggle-label input:checked+.toggle-slider:before {
    transform: translateX(16px);
}

.toggle-text {
    font-size: 13px;
    color: #334155;
    font-weight: 500;
}

/* Frequency Selector */
.frequency-selector {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #E2E8F0;
}

.selector-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #64748B;
    margin-bottom: 6px;
}

.frequency-multiselect {
    width: 100%;
}

@media (max-width: 768px) {
    .form-row-grid-2 {
        grid-template-columns: 1fr;
    }
}
</style>
